import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Grid2,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BuildIcon from "@mui/icons-material/Build";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import {
  NoDataMatchStyles,
  commonHeaderStyle,
  commonRowStyle
} from "../../assets/styles/CommonStyles";
import {
  CreateInvoiceResponse,
  InvoiceColumns,
  InvoiceDetails
} from "../../interfaces/Invoicing.interfaces";
import CustomTextField from "../textfield/CustomTextField";
import { InvoiceMode, InvoiceStatus } from "../../constants/constants";
import { RemoveRedEye } from "@mui/icons-material";
import CustomBackdrop from "../backdrop/CustomBackdrop";
import InvoiceDetailsDialog from "../dialog/InvoiceDetailsDialog";
import useApi from "../../hooks/useApi";
import dayjs from "dayjs";

const sectionIcons: Record<string, React.ReactElement> = {
  "This Month": <CalendarMonthIcon sx={{ fontSize: "26px", color: "#1e66ab" }} />,
  "PO's": <ReceiptLongIcon sx={{ fontSize: "26px", color: "#1e66ab" }} />,
  "Instructed Works Summary": <ListAltIcon sx={{ fontSize: "26px", color: "#1e66ab" }} />,
  "AFP": <AccountBalanceIcon sx={{ fontSize: "26px", color: "#1e66ab" }} />,
  "WIP": <BuildIcon sx={{ fontSize: "26px", color: "#1e66ab" }} />,
  "Pre Billing": <RequestQuoteIcon sx={{ fontSize: "26px", color: "#1e66ab" }} />,
};

interface InvoiceFormTableProps {
  data: CreateInvoiceResponse;
  invoiceDetails: InvoiceDetails | null;
  onChange: (data: CreateInvoiceResponse) => void;
  onValidationChange?: (isValid: boolean) => void;
  mode?: InvoiceMode;
  ActiveSection?: number;
  projectId: number;
  invoiceId?:number;
  netIntoAccounts:number;
}

const InvoiceFormTable: React.FC<InvoiceFormTableProps> = ({
  data,
  onChange,
  invoiceDetails,
  onValidationChange,
  mode,
  ActiveSection,
  projectId, 
  invoiceId,
  netIntoAccounts
}) => {

  const [editedRows, setEditedRows] = useState<Record<string, any>>({});
  const [validationMessages, setValidationMessages] = useState<
    Record<string, Record<string, string>>
  >({});
 

useEffect(() => {
  if (!Array.isArray(data?.sections)) return;

  // ✅ If Approved — already includes this invoice, show as-is
  if (invoiceDetails?.status === InvoiceStatus.Approved) return;

  let totalFromThisInvoice = 0;

  const updatedSections = data.sections.map(section => {
    const isThisMonthSection = section.sectionId === 4;

    return {
      ...section,
      rows: section.rows.map((row: any, index: number) => {
        const rowKey = `${data.projectId}-${section.sectionId}-${index}`;
        const merged = { ...row, ...(editedRows[rowKey] || {}) };

        // ✅ Only sum This Month section
        if (isThisMonthSection) {
          const getVal = (key: string) => getNumber(merged[key]);

          totalFromThisInvoice +=
            (getVal("amountToInvoice") +
              getVal("amountToAFPAccrual") +
              getVal("amountToWIPAccrual") +
              getVal("amountToPrebillRelease") +
              getVal("amountToPrebillWriteOff")) -
            (getVal("amountToCredit") +
              getVal("amountToAFPRelease") +
              getVal("amountToAFPWriteOff") +
              getVal("amountToWIPRelease") +
              getVal("amountToWIPWriteOff") +
              getVal("amountToPrebillAccrual"));
        }

        return merged;
      })
    };
  });

  const newNetIntoAccounts = netIntoAccounts + totalFromThisInvoice;
  const forecasted = getNumber(data.financialSummary?.forecasted);
  const newDifference = forecasted - newNetIntoAccounts;

  onChange({
    ...data,
    sections: updatedSections,
    netIntoAccounts: newNetIntoAccounts,
    differnce: newDifference
  });

}, [editedRows, data.projectId]); // ✅ runs on mount when data loads AND on every edit
// useEffect(() => {
//     if (!Array.isArray(data?.sections)) return;

//     const updatedSections = data.sections.map(section => ({
//       ...section,
//       rows: section.rows.map((row: any, index: number) => {
//         const rowKey = `${data.projectId}-${section.sectionId}-${index}`;
//         return {
//           ...row,
//           ...(editedRows[rowKey] || {})
//         };
//       })
//     }));

//     onChange({
//       ...data,
//       sections: updatedSections
//     });
//   }, [editedRows]);

  useEffect(() => {
    setEditedRows({});
    setValidationMessages({});
  }, [data.projectId]);
  // Inside InvoiceFormTable component
  useEffect(() => {
    // Check if any row in validationMessages has any error strings
    const hasErrors = Object.values(validationMessages).some(rowErrors =>
      Object.values(rowErrors).some(message => message !== "")
    );


    onValidationChange?.(!hasErrors);
  }, [validationMessages, onValidationChange]);
  const { execute, loading } = useApi();

  const [columnWidths, setColumnWidths] = useState<{

    [key: string]: number;

  }>({});

  const [dialogData, setDialogData] = useState([]);
  const [dialogOpen, setdialogOpen] = useState(false);
  const [SectionRows, setSectionRows] = useState<any>([]);
  const [FeeStageID, setFeeStageID] = useState<any[] | null>();
  const [FeeStageIDs, setFeeStageIDs] = useState<number[]>([]);
  const startResizing = (
    columnId: number,
    startX: number
  ) => {
    const startWidth =
      columnWidths[columnId] || 150;

    const onMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startX;

      const newWidth =
        Math.max(80, startWidth + delta);

      setColumnWidths(prev => ({
        ...prev,
        [columnId]: newWidth
      }));
    };

    const onMouseUp = () => {
      document.removeEventListener(
        "mousemove",
        onMouseMove
      );
      document.removeEventListener(
        "mouseup",
        onMouseUp
      );
    };

    document.addEventListener(
      "mousemove",
      onMouseMove
    );

    document.addEventListener(
      "mouseup",
      onMouseUp
    );
  };

  const GetFeeStageDetails = async (feeStageID: number, projectId: number,invoiceId?:number) => {
    const { data } = await execute<any>(
      'get',
      'invoice/getFeeStageDetails',
      {
        projectId, feeStageID, invoiceId
      }
    );
    setDialogData(data);
    setdialogOpen(true)
  }
  useEffect(() => {
    if (!data?.sections) return;
    const ids: number[] = [];
    data.sections.forEach(section => {
      if (section.sectionId === ActiveSection ) {
        section.rows.forEach(row => {
          ids.push(row.feeStageId);
        });
      }  
    });
    setFeeStageIDs(ids);
  }, [ActiveSection,data]);



  const getNumber = (val: any) => {
    const n = Number(val);
    return isNaN(n) ? 0 : n;
  };
  const validateAFPWriteOff = (
    rowKey: string,
    row: any,
    value: any,
    fieldKey: string
  ) => {

    const afpBalance =getNumber(row.afpBalance);

    let message = "";

    if (value > afpBalance) {
      message = `Value can not be greater than AFP Balance ${afpBalance}`;
    }

    setValidationMessages(prev => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || {}),
        [fieldKey]: message
      }
    }));
  };
  const validateAFPWriteOffReason = (
  rowKey: string,
  row: any,
  currentAmount: number,
  currentReason: string
) => {
  const key = "afpWriteOffReason";

  setValidationMessages(prev => {
    const rowErrors = { ...(prev[rowKey] || {}) };

    if (currentAmount > 0 && !currentReason.trim()) {
      rowErrors[key] = "AFP Write Off Reason is required";
    } else {
      delete rowErrors[key];
    }

    return {
      ...prev,
      [rowKey]: rowErrors
    };
  });
};
  const validateAFPRelease = (
    rowKey: string,
    row: any,
    value: any,
    fieldKey: string
  ) => {

    const afpBalance =getNumber(row.afpBalance);

    let message = "";

    if (value > afpBalance) {
      message = `Value can not be greater than AFP Balance ${afpBalance}`;
    }

    setValidationMessages(prev => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || {}),
        [fieldKey]: message
      }
    }));
  };
  const validateAFPNumber = (
    rowKey: string,
    currentAccrual: number,
    currentAFPNumber: any 
  ) => {
    const key = "afpNumber";
    const afpStr = String(currentAFPNumber || "").trim();

    setValidationMessages(prev => {
      const rowErrors = { ...(prev[rowKey] || {}) };

      if (currentAccrual > 0 && !afpStr) {
        rowErrors[key] = "AFP Number is required";
      } else {
        // THIS IS CRITICAL: Explicitly remove the error
        delete rowErrors[key];
      }

      return {
        ...prev,
        [rowKey]: rowErrors
      };
    });
  };
  const calculateRowFields = (
  updatedRow: any
): Record<string, number> => {

  const getVal = (key: string) =>
    getNumber(updatedRow[key]);

  const additions =
    getVal("amountToInvoice") +
    getVal("amountToAFPAccrual") +
    getVal("amountToWIPAccrual") +
    getVal("amountToPrebillRelease") +
    getVal("amountToPrebillWriteOff");

  const deductions =
    getVal("amountToCredit") +
    getVal("amountToAFPRelease") +
    getVal("amountToAFPWriteOff") +
    getVal("amountToWIPRelease") +
    getVal("amountToWIPWriteOff") +
    getVal("amountToPrebillAccrual");

  const netValue = additions - deductions;
  const inflows =
    getVal("amountToInvoice") +
    getVal("amountToAFPAccrual") +
    getVal("amountToWIPAccrual") +
    getVal("amountToPrebillRelease") +
    getVal("amountToPrebillWriteOff");

  const outflows =
    getVal("amountToCredit") +
    getVal("amountToAFPRelease") +
    getVal("amountToAFPWriteOff") +
    getVal("amountToWIPRelease") +
    getVal("amountToWIPWriteOff") +
    getVal("amountToPrebillAccrual");

  const netIntoAccounts    = inflows - outflows;
  const dropOutFromForecast =
    getVal("forecasted") - netValue;

  return {
    dropOutFromForecast,
    netIntoAccounts 
  };
};
  const validateAmountToAFPAccrual = (
    rowKey: string,
    instructed: number,
    value: any,
    fieldKey: string
  ) => {
    const val = getNumber(value);

    let message = "";

    if (val > instructed) {
      message = `Must be less than instructed (${instructed})`;
    } 
    setValidationMessages(prev => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || {}),
        [fieldKey]: message
      }
    }));
  };
  const validateCreditNoteReason = (
    rowKey: string,
    row: any,
    currentAmount: number, 
    currentReason: string  
  ) => {
    const key = "creditNoteReason";

    setValidationMessages(prev => {
      const rowErrors = { ...(prev[rowKey] || {}) };

      // Use the values passed as arguments directly
      if (currentAmount > 0 && !currentReason.trim()) {
        rowErrors[key] = "Credit Note Reason is required";
      } else {
        delete rowErrors[key];
      }

      return {
        ...prev,
        [rowKey]: rowErrors
      };
    });
  };
  const validateAmountToCredit = (
    rowKey: string,
    row: any,
    value: any,
    fieldKey: string,
    editedRow: any
  ) => {
    const val = getNumber(value);
    const prebillWriteOff = getNumber(
    editedRow?.amountToPrebillWriteOff ?? row.amountToPrebillWriteOff
  );

    let message = "";

    if (val < 0) {
      message = "Value cannot be negative";
    }
    else if (val < prebillWriteOff) {
      message = `Minimun value should be (${prebillWriteOff})`;
    }

    setValidationMessages(prev => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || {}),
        [fieldKey]: message
      }
    }));
  };
  const validateAmountToInvoice = (
    rowKey: string,
    row: any,
    value: any,
    fieldKey: string
  ) => {
    const val = getNumber(value);
    const diff = getNumber(row.budgetRemaining);
    const afpRelease =
      getNumber(editedRows[rowKey]?.amountToAFPRelease) ||
      getNumber(row.amountToAFPRelease);

    const wipRelease =
      getNumber(editedRows[rowKey]?.amountToWIPRelease) ||
      getNumber(row.amountToWIPRelease);

    const prebillAccrual =
      getNumber(editedRows[rowKey]?.amountToPrebillAccrual) ||
      getNumber(row.amountToPrebillAccrual);
    let message = "";

    if (val < 0) {
      message = "Value cannot be negative";
    }
    else if (val < afpRelease + wipRelease + prebillAccrual) {
      message = `Value should have min of sum of Afp Relase wip Release and prebill Accrual ${afpRelease + wipRelease + prebillAccrual}`;
    }
    else if (val > diff) {
      message = `Value cannot exceed ${diff}`;
    }


    setValidationMessages(prev => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || {}),
        [fieldKey]: message
      }
    }));
  };
  const validateAmountToWIPAccrual = (
  rowKey: string,
  instructed: number,
  value: any,
  fieldKey: string
) => {
  const val = getNumber(value);

  let message = "";

  
  if (val > instructed) {
    message = `Must be less than instructed (${instructed})`;
  }

  setValidationMessages(prev => ({
    ...prev,
    [rowKey]: {
      ...(prev[rowKey] || {}),
      [fieldKey]: message
    }
  }));
};
const validateWIPReason = (
  rowKey: string,
  row: any,
  currentAmount: number,
  currentReason: string
) => {
  const key = "wipReason";

  setValidationMessages(prev => {
    const rowErrors = { ...(prev[rowKey] || {}) };

    if (currentAmount !== 0 && !currentReason.trim()) {
      rowErrors[key] = "WIP Reason is required";
    } else {
      delete rowErrors[key];
    }

    return {
      ...prev,
      [rowKey]: rowErrors
    };
  });
};
const validateAmountToWIPRelease = (
  rowKey: string,
  row: any,
  value: any,
  fieldKey: string
) => {
  const val = getNumber(value);

  const wipBalance =getNumber(row.wipBalance);

  let message = "";

  // Negative values are allowed
  if (val > wipBalance) {
    message = `Value cannot be greater than WIP Balance (${wipBalance})`;
  }

  setValidationMessages(prev => ({
    ...prev,
    [rowKey]: {
      ...(prev[rowKey] || {}),
      [fieldKey]: message
    }
  }));
};
const validateAmountToWIPWriteOff = (
  rowKey: string,
  row: any,
  value: any,
  fieldKey: string
) => {
  const val = getNumber(value);

  const wipBalance = getNumber(row.wipBalance);

  let message = "";

  // Negative values are allowed
  if (val > wipBalance) {
    message = `Value cannot be greater than WIP Balance (${wipBalance})`;
  }

  setValidationMessages(prev => ({
    ...prev,
    [rowKey]: {
      ...(prev[rowKey] || {}),
      [fieldKey]: message
    }
  }));
};
const validateWIPWriteOffReason = (
  rowKey: string,
  row: any,
  currentAmount: number,
  currentReason: string
) => {
  const key = "wipWriteOffReason";

  setValidationMessages(prev => {
    const rowErrors = { ...(prev[rowKey] || {}) };

    if (currentAmount !== 0 && !currentReason.trim()) {
      rowErrors[key] = "WIP Write-off Reason is required";
    } else {
      delete rowErrors[key];
    }

    return {
      ...prev,
      [rowKey]: rowErrors
    };
  });
};
const validateAmountToPrebillAccrual = (
  rowKey: string,
  instructed: number,
  value: any,
  fieldKey: string
) => {
  const val = getNumber(value);

  let message = "";

  // Negative values are allowed
  if (val > instructed) {
    message = `Must be less than instructed (${instructed})`;
  }
   else if(val<0){
    message=`Value can not be Negative`
  }


  setValidationMessages(prev => ({
    ...prev,
    [rowKey]: {
      ...(prev[rowKey] || {}),
      [fieldKey]: message
    }
  }));
};
const validatePrebillReason = (
  rowKey: string,
  row: any,
  currentAmount: number,
  currentReason: string
) => {
  const key = "prebillReason";

  setValidationMessages(prev => {
    const rowErrors = { ...(prev[rowKey] || {}) };

    if (currentAmount !== 0 && !currentReason.trim()) {
      rowErrors[key] = "Prebill Reason is required";
    } else {
      delete rowErrors[key];
    }

    return {
      ...prev,
      [rowKey]: rowErrors
    };
  });
};
const validateAmountToPrebillRelease = (
  rowKey: string,
  row: any,
  value: any,
  fieldKey: string
) => {
  const val = getNumber(value);

  const prebillBalance =getNumber(row.prebillBalance);

  let message = "";

  if (val > prebillBalance) {
    message = `Value cannot be greater than Prebill Balance (${prebillBalance})`;
  }
  else if(val<0){
    message=`Value can not be Negative`
  }

  setValidationMessages(prev => ({
    ...prev,
    [rowKey]: {
      ...(prev[rowKey] || {}),
      [fieldKey]: message
    }
  }));
};
const validateAmountToPrebillWriteOff = (
  rowKey: string,
  row: any,
  value: any,
  fieldKey: string
) => {
  const val = getNumber(value);

  const prebillBalance =getNumber(row.prebillBalance);

  let message = "";

  // Negative values are allowed
  if (val > prebillBalance) {
    message = `Value cannot be greater than Prebill Balance (${prebillBalance})`;
  }
   else if(val<0){
    message=`Value can not be Negative`
  }


  setValidationMessages(prev => ({
    ...prev,
    [rowKey]: {
      ...(prev[rowKey] || {}),
      [fieldKey]: message
    }
  }));
};
const validatePrebillWriteOffReason = (
  rowKey: string,
  row: any,
  currentAmount: number,
  currentReason: string
) => {
  const key = "prebillWriteOffReason";

  setValidationMessages(prev => {
    const rowErrors = { ...(prev[rowKey] || {}) };

    if (currentAmount !== 0 && !currentReason.trim()) {
      rowErrors[key] = "Prebill Write-off Reason is required";
    } else {
      delete rowErrors[key];
    }

    return {
      ...prev,
      [rowKey]: rowErrors
    };
  });
};
  const checkValidation = (
    rowKey: string,
    row: any,
    sourceColName: string,
    value: any
  ) => {
    const key = sourceColName;

    const editedRow = editedRows[rowKey] || {};

    switch (key) {
      case "amountToInvoice":
        validateAmountToInvoice(rowKey, row, value, key);
        break;
      case "amountToCredit":

        validateAmountToCredit(rowKey, row, value, key, editedRow);


        validateCreditNoteReason(
          rowKey,
          row,
          getNumber(value),
          String(editedRow?.creditNoteReason ?? row.creditNoteReason ?? "")
        );
        break;
      case "amountToAFPAccrual":
        validateAmountToAFPAccrual(rowKey, getNumber(row.instructed), value, key);

        
        validateAFPNumber(
          rowKey,
          getNumber(value),
          editedRow.afpNumber ?? row.afpNumber
        );
        break;

      case "afpNumber":
        // We pass 'value' as the AFP number because that's what just changed
        validateAFPNumber(
          rowKey,
          getNumber(editedRow.amountToAFPAccrual ?? row.amountToAFPAccrual),
          value
        );
        break;
      case "amountToCredit":
        // 1. Validate the amount itself (negative or min-value checks)
        validateAmountToCredit(rowKey, row, value, key, editedRow);

        // 2. Trigger Reason validation because amount changed
        validateCreditNoteReason(
          rowKey,
          row,
          getNumber(value),
          String(editedRow?.creditNoteReason ?? row.creditNoteReason ?? "")
        );
        break;

      case "creditNoteReason":
        // Validate reason whenever it is typed
        validateCreditNoteReason(
          rowKey,
          row,
          getNumber(editedRow?.amountToCredit ?? row.amountToCredit),
          String(value)
        );
        break;
      case "amountToAFPAccrual":
        validateAmountToAFPAccrual(rowKey, getNumber(row.instructed), value, key);

        // Re-validate AFP Number whenever Accrual changes
        validateAFPNumber(
          rowKey,
          getNumber(value), // The new accrual value
          String(editedRow?.afpNumber ?? row.afpNumber ?? "") // The existing number
        );
        break;

      case "afpNumber":
        // Validate AFP Number whenever the user types in it
        validateAFPNumber(
          rowKey,
          getNumber(editedRow?.amountToAFPAccrual ?? row.amountToAFPAccrual), // Existing accrual
          String(value)
        );
        break;
      case "amountToAFPRelease":
        validateAmountToInvoice(
          rowKey,
          row,
          editedRow?.amountToInvoice ?? row.amountToInvoice,
          "amountToInvoice"
        );
        validateAFPRelease(rowKey, row, value, key);
        break;
     case "amountToAFPWriteOff":
      validateAFPWriteOff(rowKey, row, value, key);

      validateAFPWriteOffReason(
        rowKey,
        row,
        getNumber(value),
        String(
          editedRow?.afpWriteOffReason ??
          row.afpWriteOffReason ??
          ""
        )
      );
      break;
      case "afpWriteOffReason":
      validateAFPWriteOffReason(
        rowKey,
        row,
        getNumber(
          editedRow?.amountToAFPWriteOff ??
          row.amountToAFPWriteOff
        ),
        String(value)
      );
      
      break;
      case "amountToWIPAccrual":
  validateAmountToWIPAccrual(
    rowKey,
    getNumber(row.instructed),
    value,
    key
  );
    validateWIPReason(
    rowKey,
    row,
    getNumber(value),
    String(
      editedRow?.wipReason ??
      row.wipReason ??
      ""
    )
  );
  break;
  case "wipReason":
  validateWIPReason(
    rowKey,
    row,
    getNumber(
      editedRow?.amountToWIPAccrual ??
      row.amountToWIPAccrual
    ),
    String(value)
  );
  break;
  case "amountToWIPRelease":
    validateAmountToInvoice(
          rowKey,
          row,
          editedRow?.amountToInvoice ?? row.amountToInvoice,
          "amountToInvoice"
        );
  validateAmountToWIPRelease(
    rowKey,
    row,
    value,
    key
  );
  break;
  case "amountToWIPWriteOff":
  validateAmountToWIPWriteOff(
    rowKey,
    row,
    value,
    key
  );

  validateWIPWriteOffReason(
    rowKey,
    row,
    getNumber(value),
    String(
      editedRow?.wipWriteOffReason ??
      row.wipWriteOffReason ??
      ""
    )
  );
  break;
  case "wipWriteOffReason":
  validateWIPWriteOffReason(
    rowKey,
    row,
    getNumber(
      editedRow?.amountToWIPWriteOff ??
      row.amountToWIPWriteOff
    ),
    String(value)
  );
  break;
  case "amountToPrebillAccrual":
    validateAmountToInvoice(
          rowKey,
          row,
          editedRow?.amountToInvoice ?? row.amountToInvoice,
          "amountToInvoice"
        );
  validateAmountToPrebillAccrual(
    rowKey,
    getNumber(row.instructed),
    value,
    key
  );

  validatePrebillReason(
    rowKey,
    row,
    getNumber(value),
    String(
      editedRow?.prebillReason ??
      row.prebillReason ??
      ""
    )
  );
  break;
  case "prebillReason":
  validatePrebillReason(
    rowKey,
    row,
    getNumber(
      editedRow?.amountToPrebillAccrual ??
      row.amountToPrebillAccrual
    ),
    String(value)
  );
  break;
  case "amountToPrebillRelease":
  validateAmountToPrebillRelease(
    rowKey,
    row,
    value,
    key
  );
  break;
  case "amountToPrebillWriteOff":
  validateAmountToPrebillWriteOff(
    rowKey,
    row,
    value,
    key
  );

  validatePrebillWriteOffReason(
    rowKey,
    row,
    getNumber(value),
    String(
      editedRow?.prebillWriteOffReason ??
      row.prebillWriteOffReason ??
      ""
    )
  );
  break;
  case "prebillWriteOffReason":
  validatePrebillWriteOffReason(
    rowKey,
    row,
    getNumber(
      editedRow?.amountToPrebillWriteOff ??
      row.amountToPrebillWriteOff
    ),
    String(value)
  );
  break;
      default:
        break;
    }
  };

  const handleChange = (
    row: any,
    rowKey: string,
    sourceColName: string,
    value: any,
    dataType: string
  ) => {
    let val: any = value;

    if (dataType?.toLowerCase() === "number") {
      val = value === "" ? 0 : Number(value);
    }
    const updatedRow = {
    ...row,
    ...(editedRows[rowKey] || {}),
    [sourceColName]: val
  };
  const calculatedFields = calculateRowFields(updatedRow);
    setEditedRows(prev => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] || {}),
        [sourceColName]: val,
         ...calculatedFields

      }
    }));

    checkValidation(rowKey, row, sourceColName, value);
  };
 const GetAFPDetails=async( feeStageId:number, projectId: number)=>{
  

    const {data}=await execute<any>( 'get',
      'invoice/GetAFPDetails',
      {
        projectId,
        feeStageId
      }
      
    )
    console.log(data);
    setDialogData(data);
    setdialogOpen(true)
 }
  
  const createRowCell = (
    row: any,
    columns: InvoiceColumns[],
    rowKey: string,
    getStickyBodyCell?: (colIndex: number) => object,
    options?: number[]
  ) => {
    return columns.filter(col => col.fieldName !== 'feeStageId').map((col, index) => {
      const fieldKey = col.fieldName;

      const value =
        editedRows[rowKey]?.[fieldKey] ??
        row[fieldKey] ??
        "";
      const validationKey = fieldKey;
      return (
        <>
          <TableCell key={`${rowKey}-${fieldKey}`}
            sx={{
              width:
                columnWidths[col.columnId] || 150,
              minWidth: 80,
              maxWidth:
                columnWidths[col.columnId] || 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              ...(getStickyBodyCell ? getStickyBodyCell(index) : {})
            }}>
            {col.isEditable ? (
              col.fieldName === 'afpNumber' ? (
                <Autocomplete
                  freeSolo   // allows manual typing
                  options={options || []}   // dropdown values
                  value={row.reason || ""}
                  onChange={(event, newValue) => {
                    handleChange(row, rowKey, fieldKey, newValue, col.dataType);
                  }}
                  onInputChange={(event, newInputValue) => {
                    handleChange(row, rowKey, fieldKey, newInputValue, col.dataType);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="0"
                      error={!!validationMessages[rowKey]?.[validationKey]}
      helperText={validationMessages[rowKey]?.[validationKey] || ""}
                      sx={{
                        width: { md: "200px" },
                        "& .MuiInputBase-root": {
                          height: "40px",
                          "& .MuiInputBase-input::placeholder": {
                            color: "black",
                            opacity: 1   // important — otherwise MUI makes it faded
                          }
                        }
                      }}
                    />
                  )}
                />
              ) : (
                <TextField
                  type={
                    col.dataType?.toLowerCase() === "number"
                      ? "number"
                      : "text"
                  }
                  placeholder={
                    col.dataType?.toLowerCase() === "number"
                      ? "0"
                      : "Enter text"
                  }
                   sx={{
                        "& .MuiInputBase-root": {
                          height: "40px",
                          "& .MuiInputBase-input::placeholder": {
                            color: col.dataType?.toLowerCase() === "number"
                      ? "black"
                      : "gray",
                            opacity: 1   // important — otherwise MUI makes it faded
                          }
                        }
                      }}

                  value={value === 0 ? "" : value}
                  onChange={e => {
                    let val: any = e.target.value;
                    if (col.dataType?.toLowerCase() === "number") {
                      if (val.includes(".")) {
                        const [whole, decimal] = val.split(".");
                        if (decimal.length > 2) {
                          val = `${whole}.${decimal.slice(0, 2)}`;
                        }
                      }
                    }
                    if (col.dataType?.toLowerCase() === "number") {
                      val = val === "" ? "" : Number(val);
                    }
                    handleChange(
                      row,
                      rowKey,
                      fieldKey,
                      val,
                      col.dataType
                    );
                  }}
                  onKeyDown={e => {
                    if (e.key === "-") e.preventDefault();
                  }}
                  size="small"
                  fullWidth
                  disabled={mode === InvoiceMode.View}
                  error={!!validationMessages[rowKey]?.[validationKey]}
                  helperText={validationMessages[rowKey]?.[validationKey] || ""}
                />
              )
            ) : (
              typeof value === "string" &&
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
                ? dayjs(value).format("DD/MM/YYYY")
                : value ?? ""
            )}

          </TableCell>
        </>
      );
    });

  };
  // First 3 columns are frozen: Code, Description, Instructed
  const STICKY_COLS = [
    { width: 80, left: 0 },
    { width: 160, left: 80 },
    { width: 110, left: 240 },
  ];

  const stickyHeaderSx = (colIndex: number) =>
    colIndex < STICKY_COLS.length
      ? {
        position: "sticky" as const,
        left: STICKY_COLS[colIndex].left,
        minWidth: STICKY_COLS[colIndex].width,
        width: STICKY_COLS[colIndex].width,
        backgroundColor: "#92d050",
      }
      : { whiteSpace: "nowrap" as const };

  // Inline style wins over commonHeaderStyle's "& th" zIndex:2
  const stickyHeaderStyle = (colIndex: number): React.CSSProperties =>
    colIndex < STICKY_COLS.length ? { zIndex: 6 } : {};

  const stickyBodyCell = (colIndex: number) =>
    colIndex < STICKY_COLS.length
      ? {
        position: "sticky" as const,
        left: STICKY_COLS[colIndex].left,
        minWidth: STICKY_COLS[colIndex].width,
        width: STICKY_COLS[colIndex].width,
        zIndex: 2,
        backgroundColor: "#fff",
      }
      : {};

  return (
    // Block (non-flex) wrapper — naturally constrained to page width,
    // which allows TableContainer's overflow:auto to trigger horizontal scroll.
    <Box>
      {data.sections.map(section => (
        ActiveSection === section.sectionId &&
        <Box key={section.sectionId}>
          {/* Section name sits above the scroll container — no sticky needed */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            {sectionIcons[section.sectionName]}
            <Typography
              sx={{
                fontSize: "25px",
                color: "#1e66ab",
                fontWeight: "bold",
              }}
            >
              {section.sectionName}
            </Typography>
          </Box>

          {/* Single scroll container for both horizontal and vertical scroll */}
          <TableContainer sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "350px" }}>
            <Table sx={{
              borderCollapse: "separate", borderSpacing: 0, minWidth: "1500px", tableLayout: "fixed",
              width: "max-content",
            }}>
              <TableHead>
                <TableRow sx={commonHeaderStyle}>
                  {section.columns
                    .filter(col => col.fieldName !== 'feeStageId')
                    .map((col, colIndex) => (
                      <TableCell
                        key={col.columnId}
                        sx={{
                          whiteSpace: "nowrap",
                          ...stickyHeaderSx(colIndex),
                        }}
                        // inline style overrides the "& th" zIndex from commonHeaderStyle
                        style={stickyHeaderStyle(colIndex)}
                      >
                        {col.columnName}
                        <Box
                          onMouseDown={(e) =>
                            startResizing(
                              col.columnId,
                              e.clientX
                            )
                          }

                          sx={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            height: "100%",
                            width: "8px",
                            cursor: "col-resize",
                            userSelect: "none",
                            zIndex: 10,
                            "&:hover": {
                              backgroundColor:
                                "rgba(0,0,0,0.1)"
                            }
                          }}
                        />
                      </TableCell>
                    ))}
                  {(section.sectionId == 3 || section.sectionId===5)  && <TableCell
                  sx={{
                      width:section.sectionId == 5?100:"auto",
                    }}>
                    View Details
                  </TableCell>}
                </TableRow>
              </TableHead>

              <TableBody>
                {!section.rows?.length ? (
                  <TableRow>
                    <TableCell colSpan={section.columns.length}>
                      <Typography sx={NoDataMatchStyles}>
                        There are no rows to display
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  section.rows.map((row, index) => {
                  
                    const rowKey = `${data.projectId}-${section.sectionId}-${index}`;
                    return (
                      <TableRow key={rowKey} sx={commonRowStyle}>

                        {createRowCell(row, section.columns, rowKey, stickyBodyCell, row.afpNumbers)}

                        {(section.sectionId == 3 || section.sectionId===5) &&<TableCell

                          sx={{

                            ":hover": {

                              cursor: "pointer"

                            },

                            textAlign: "center"

                          }}
                        >
                          <IconButton

                           onClick={() => {
                            setFeeStageID(row.feeStageId);
                            
                            if (section.sectionId === 3) {
                              GetFeeStageDetails(row.feeStageId, projectId, invoiceId);
                            } else {
                              GetAFPDetails(row.feeStageId, projectId);
                            }
                          }}
                          >
                            <RemoveRedEye />
                          </IconButton>
                        </TableCell>}
                      </TableRow>

                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

        </Box>
      ))}
      
      <InvoiceDetailsDialog
        loading={loading}
        open={dialogOpen}
        data={dialogData}
        SectionId={ActiveSection}
        onClose={() => setdialogOpen(false)}
        GetFeeStageDetails={ActiveSection===5?GetAFPDetails:GetFeeStageDetails}
        CurrentFeestageID={FeeStageID}
        projectID={projectId}
        FeestageIDs={FeeStageIDs}
        invoiceId={invoiceId}
      />
      <CustomBackdrop open={loading} />
    </Box>
  );
};

export default InvoiceFormTable;
